import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase-config';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const PUBLISHABLE_KEY = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
const stripePromise = loadStripe(PUBLISHABLE_KEY || undefined);

/** Must match OwnerDashboard route for 3DS / bank redirect returns */
const OWNER_RETURN_PATH = '/owner';

function publishableKeyMode(pk) {
  if (!pk || typeof pk !== 'string') return 'unknown';
  const k = pk.trim();
  if (k.startsWith('pk_live_')) return 'live';
  if (k.startsWith('pk_test_')) return 'test';
  return 'unknown';
}

function ConfirmPaymentStep({
  estimateAmount,
  requestId,
  billingName,
  billingEmail,
  onPaymentSuccess,
  onCancel,
  onBack,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async (e) => {
    e.preventDefault();
    setError('');

    if (!stripe || !elements) {
      setError('Stripe has not finished loading. Please wait a moment.');
      return;
    }

    if (loading) return;
    setLoading(true);

    try {
      sessionStorage.setItem(
        'td_pending_payment',
        JSON.stringify({ requestId, billingName, billingEmail })
      );

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${OWNER_RETURN_PATH}`,
          receipt_email: billingEmail || undefined,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        sessionStorage.removeItem('td_pending_payment');
        const raw = stripeError.message || 'Payment failed';
        setError(
          /no such payment_intent/i.test(raw)
            ? 'Stripe could not find this payment. Your site publishable key (test vs live) must match the secret key used by Cloud Functions. Rebuild hosting with the correct REACT_APP_STRIPE_PUBLISHABLE_KEY.'
            : raw
        );
        setLoading(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        sessionStorage.removeItem('td_pending_payment');
        const cardLast4 =
          paymentIntent.charges?.data?.[0]?.payment_method_details?.card?.last4 || null;

        const finalize = httpsCallable(functions, 'finalizePayment');
        const resp = await finalize({
          paymentIntentId: paymentIntent.id,
          requestId,
          billingName,
          billingEmail,
          cardLast4,
        });

        if (!resp.data || !resp.data.success) {
          setError('Payment finalization failed on server: ' + (resp.data?.error || 'Unknown error'));
          setLoading(false);
          return;
        }

        onPaymentSuccess({
          paymentMethodId: paymentIntent.id,
          amount: estimateAmount,
          requestId,
          transactionId: resp.data.transactionId || paymentIntent.id,
          billingDetails: { name: billingName, email: billingEmail },
        });
      } else {
        sessionStorage.removeItem('td_pending_payment');
        setError('Payment did not complete. Please try again.');
      }
    } catch (err) {
      sessionStorage.removeItem('td_pending_payment');
      console.error('Payment error:', err);
      setError(err.message || 'Payment failed');
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handlePay}>
      <div style={{ marginBottom: 16 }}>
        <PaymentElement
          options={{
            layout: 'accordion',
            defaultValues: {
              billingDetails: {
                name: billingName,
                email: billingEmail,
              },
            },
          }}
        />
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#c00', padding: 12, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          style={{ flex: 1, minWidth: 100, padding: 12, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, fontWeight: 600 }}
        >
          Back
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          style={{ flex: 1, minWidth: 100, padding: 12, background: '#e5e7eb', color: '#333', border: 'none', borderRadius: 6, fontWeight: 600 }}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !stripe}
          style={{ flex: 1, minWidth: 120, padding: 12, background: loading || !stripe ? '#ccc' : '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}
        >
          {loading ? 'Processing…' : `Pay $${parseFloat(estimateAmount).toFixed(2)}`}
        </button>
      </div>
    </form>
  );
}

function PaymentModal({ estimateAmount, requestId, onPaymentSuccess, onCancel, professionalName, professionalId }) {
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [error, setError] = useState('');
  const [preparing, setPreparing] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  const handleBillingContinue = async (e) => {
    e.preventDefault();
    if (!billingName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!billingEmail.trim()) {
      setError('Please enter your email');
      return;
    }
    if (!PUBLISHABLE_KEY) {
      setError('Stripe is not configured (missing publishable key).');
      return;
    }

    setError('');
    setPreparing(true);

    try {
      const createIntent = httpsCallable(functions, 'createPaymentIntent');
      const intentData = { amount: estimateAmount, requestId };
      if (professionalId) {
        intentData.professionalId = professionalId;
      }
      const intentResp = await createIntent(intentData);
      const payload = intentResp?.data?.result ?? intentResp?.data ?? null;
      if (!payload || !payload.clientSecret) {
        throw new Error('Missing clientSecret from createPaymentIntent response');
      }

      const clientSecretTrimmed = String(payload.clientSecret).trim();
      const { stripeMode } = payload;
      const pkMode = publishableKeyMode(PUBLISHABLE_KEY);
      if (
        stripeMode &&
        stripeMode !== 'unknown' &&
        pkMode !== 'unknown' &&
        stripeMode !== pkMode
      ) {
        throw new Error(
          `Stripe test/live mismatch: this site uses a ${pkMode} publishable key but Cloud Functions used a ${stripeMode} secret. Use matching keys and rebuild.`
        );
      }

      const stripe = await stripePromise;
      if (stripe) {
        const retrieved = await stripe.retrievePaymentIntent(clientSecretTrimmed);
        if (retrieved.error) {
          const m = retrieved.error.message || '';
          if (/no such payment_intent/i.test(m)) {
            throw new Error(
              'This browser cannot see the payment Stripe created (usually test vs live key mismatch). Match REACT_APP_STRIPE_PUBLISHABLE_KEY to your Cloud Functions Stripe secret, then rebuild and redeploy hosting.'
            );
          }
          throw new Error(m);
        }
      }

      setClientSecret(clientSecretTrimmed);
    } catch (err) {
      console.error('Prepare payment error:', err);
      setError(err.message || 'Could not start payment.');
    }

    setPreparing(false);
  };

  const handleBackFromCard = () => {
    setClientSecret(null);
    setError('');
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 500, width: '90%', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <h2 style={{ margin: '0 0 8px 0', fontSize: 20 }}>Accept Estimate & Collect Payment</h2>
        <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#666' }}>Professional: <strong>{professionalName}</strong></p>

        <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 14, color: '#666' }}>Estimate Amount:</p>
          <p style={{ margin: '4px 0 0 0', fontSize: 24, fontWeight: 600, color: '#333' }}>${parseFloat(estimateAmount).toFixed(2)}</p>
        </div>

        {!clientSecret ? (
          <form onSubmit={handleBillingContinue}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Full Name *</label>
              <input value={billingName} onChange={(e) => setBillingName(e.target.value)} placeholder="John Doe" style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Email Address *</label>
              <input value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} placeholder="john@example.com" style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box' }} />
            </div>

            {error && (
              <div style={{ background: '#fee', color: '#c00', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onCancel} disabled={preparing} style={{ flex: 1, padding: 12, background: '#e5e7eb', color: '#333', border: 'none', borderRadius: 6, fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={preparing} style={{ flex: 1, padding: 12, background: preparing ? '#9ca3af' : '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}>
                {preparing ? 'Preparing…' : 'Continue to payment'}
              </button>
            </div>
          </form>
        ) : (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <ConfirmPaymentStep
              estimateAmount={estimateAmount}
              requestId={requestId}
              billingName={billingName}
              billingEmail={billingEmail}
              onPaymentSuccess={onPaymentSuccess}
              onCancel={onCancel}
              onBack={handleBackFromCard}
            />
          </Elements>
        )}

        <p style={{ margin: '12px 0 0 0', fontSize: 11, color: '#999' }}>
          Secured by Stripe. Available methods (card, wallets, Link, etc.) depend on your region and Stripe settings.
        </p>
      </div>
    </div>
  );
}

export default PaymentModal;
